import moment from "moment-timezone";
import { OtcLayerAmendmentType, OtcRollDirection } from "@/lib/api/otc";
import { formatDateWithoutTimezone } from "@/lib/api/date";
import { CommonDestinationTimezone } from "@/lib/api/utils";

/**
 * Shared editable inputs every amendment supports. The backend requires a
 * complete adjustment object (option premium, contract fee, adjusted date) for
 * the layer's assigned slot on every amendment.
 */
export interface AmendmentSharedInput {
  notes?: string;
  has_invoice_fees?: boolean;
  has_contract_adjustment?: boolean;
  amendment_trader_id?: string;
  adjustment: {
    option_premium?: string;
    contract_fee?: string;
    adjusted_date?: Date;
  };
}

export interface ExitAmendmentInput extends AmendmentSharedInput {
  exit_date?: Date;
}

export interface RollAmendmentInput extends AmendmentSharedInput {
  roll_direction: OtcRollDirection;
  rolled_option_month?: string;
  carry?: string;
  rolled_basis_month?: string;
  basis_carry?: string;
  shipment_start_date?: Date;
  shipment_end_date?: Date;
  crop_year?: number;
  // Canola futures inputs (only relevant for Canola contracts).
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
}

/** Serializes a Date to the API ISO convention shared with the OTC forms. */
export function amendmentDateToApiString(value: Date | undefined): string | undefined {
  if (!value) return undefined;
  return moment
    .tz(formatDateWithoutTimezone(value), "YYYY/MM/DD", CommonDestinationTimezone)
    .toISOString();
}

function emptyToUndefined(value: string | undefined): string | undefined {
  if (value == null) return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Builds the `adjustment` object plus the common amendment fields. Empty
 * strings collapse to `undefined` so the backend's required-field validation
 * surfaces clearly rather than receiving blank values.
 */
export function buildSharedAmendmentPayload(
  input: AmendmentSharedInput,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    adjustment: {
      option_premium: emptyToUndefined(input.adjustment.option_premium),
      contract_fee: emptyToUndefined(input.adjustment.contract_fee),
      adjusted_date: amendmentDateToApiString(input.adjustment.adjusted_date),
    },
  };

  const notes = emptyToUndefined(input.notes);
  if (notes !== undefined) payload.notes = notes;
  if (input.has_invoice_fees !== undefined) {
    payload.has_invoice_fees = input.has_invoice_fees;
  }
  if (input.has_contract_adjustment !== undefined) {
    payload.has_contract_adjustment = input.has_contract_adjustment;
  }
  const traderId = emptyToUndefined(input.amendment_trader_id);
  if (traderId !== undefined) payload.amendment_trader_id = traderId;

  return payload;
}

/**
 * Roll fields that count as a "real" roll field. The backend rejects a roll
 * amendment that supplies none of these, so the wizard should also require at
 * least one before enabling submit.
 */
export const ROLL_REQUIRED_ANY_FIELDS: ReadonlyArray<keyof RollAmendmentInput> = [
  "rolled_option_month",
  "carry",
  "rolled_basis_month",
  "basis_carry",
  "shipment_start_date",
  "shipment_end_date",
  "crop_year",
];

export function rollHasAnyRealField(input: RollAmendmentInput): boolean {
  return ROLL_REQUIRED_ANY_FIELDS.some((key: keyof RollAmendmentInput) => {
    const value = input[key];
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
}

/** Builds the full request body for a roll amendment. */
export function buildRollAmendmentRequest(
  expected_current_layer_id: string,
  input: RollAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  roll_direction: OtcRollDirection;
  payload: Record<string, unknown>;
} {
  const payload: Record<string, unknown> = {
    ...buildSharedAmendmentPayload(input),
    roll_direction: input.roll_direction,
  };

  const setIfPresent = (key: string, value: string | undefined) => {
    const v = emptyToUndefined(value);
    if (v !== undefined) payload[key] = v;
  };

  setIfPresent("rolled_option_month", input.rolled_option_month);
  setIfPresent("carry", input.carry);
  setIfPresent("rolled_basis_month", input.rolled_basis_month);
  setIfPresent("basis_carry", input.basis_carry);
  setIfPresent("implied_fx_amount", input.implied_fx_amount);
  setIfPresent("actual_contract_futures", input.actual_contract_futures);
  setIfPresent("actual_contract_futures_step_one", input.actual_contract_futures_step_one);
  setIfPresent("actual_contract_futures_step_two", input.actual_contract_futures_step_two);
  setIfPresent("actual_contract_futures_double_up", input.actual_contract_futures_double_up);
  setIfPresent("final_futures_price", input.final_futures_price);
  setIfPresent("final_futures_expected_price", input.final_futures_expected_price);

  const shipmentStart = amendmentDateToApiString(input.shipment_start_date);
  if (shipmentStart !== undefined) payload.shipment_start_date = shipmentStart;
  const shipmentEnd = amendmentDateToApiString(input.shipment_end_date);
  if (shipmentEnd !== undefined) payload.shipment_end_date = shipmentEnd;
  const finalDate = amendmentDateToApiString(input.final_futures_price_date);
  if (finalDate !== undefined) payload.final_futures_price_date = finalDate;

  if (input.crop_year !== undefined) payload.crop_year = input.crop_year;

  return {
    expected_current_layer_id,
    amendment_type: "roll",
    roll_direction: input.roll_direction,
    payload,
  };
}

/**
 * Fields a contract type change may set. Mirrors the backend's accepted
 * `payload.fields` keys. All optional; the backend clears fields not used by
 * the target type and validates the merged contract against the target schema.
 */
export interface ContractTypeChangeFields {
  step_one?: string;
  step_two?: string;
  step_three?: string;
  double_up?: string;
  option_month_etc_one?: string;
  account_no_b?: string;
  futures_price?: string;
  otc_futures?: string;
  weeks?: string;
  period_qty?: string;
  knockout?: string;
  start_date?: Date;
  contract_reference?: string;
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
}

export interface ContractTypeChangeAmendmentInput extends AmendmentSharedInput {
  new_contract_type: string;
  fields: ContractTypeChangeFields;
}

/** String keys of ContractTypeChangeFields (non-date fields). */
const CONTRACT_TYPE_CHANGE_STRING_FIELDS: ReadonlyArray<
  keyof ContractTypeChangeFields
> = [
  "step_one",
  "step_two",
  "step_three",
  "double_up",
  "option_month_etc_one",
  "account_no_b",
  "futures_price",
  "otc_futures",
  "weeks",
  "period_qty",
  "knockout",
  "contract_reference",
  "implied_fx_amount",
  "actual_contract_futures",
  "actual_contract_futures_step_one",
  "actual_contract_futures_step_two",
  "actual_contract_futures_double_up",
  "final_futures_price",
  "final_futures_expected_price",
];

/** Builds the full request body for a contract type change amendment. */
export function buildContractTypeChangeAmendmentRequest(
  expected_current_layer_id: string,
  input: ContractTypeChangeAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  const fields: Record<string, unknown> = {};
  for (const key of CONTRACT_TYPE_CHANGE_STRING_FIELDS) {
    const value = emptyToUndefined(input.fields[key] as string | undefined);
    if (value !== undefined) fields[key] = value;
  }
  const startDate = amendmentDateToApiString(input.fields.start_date);
  if (startDate !== undefined) fields.start_date = startDate;
  const finalDate = amendmentDateToApiString(input.fields.final_futures_price_date);
  if (finalDate !== undefined) fields.final_futures_price_date = finalDate;

  return {
    expected_current_layer_id,
    amendment_type: "contract_type_change",
    payload: {
      ...buildSharedAmendmentPayload(input),
      new_contract_type: input.new_contract_type,
      fields,
    },
  };
}

export interface SplitAmendmentInput extends AmendmentSharedInput {
  split_customer_id?: string;
  split_quantity?: string;
  /** Optional contract reference for the split child (reown contracts). */
  contract_reference?: string;
}

/** Builds the full request body for a split amendment. */
export function buildSplitAmendmentRequest(
  expected_current_layer_id: string,
  input: SplitAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  return {
    expected_current_layer_id,
    amendment_type: "split",
    payload: {
      ...buildSharedAmendmentPayload(input),
      split_customer_id: emptyToUndefined(input.split_customer_id),
      split_quantity: emptyToUndefined(input.split_quantity),
      contract_reference: emptyToUndefined(input.contract_reference),
    },
  };
}

/** Builds the full request body for an exit amendment. */
export function buildExitAmendmentRequest(
  expected_current_layer_id: string,
  input: ExitAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  return {
    expected_current_layer_id,
    amendment_type: "exit",
    payload: {
      ...buildSharedAmendmentPayload(input),
      exit_date: amendmentDateToApiString(input.exit_date),
    },
  };
}

export type { OtcRollDirection };

// ---------------------------------------------------------------------------
// futures_roll amendment
// ---------------------------------------------------------------------------

export interface FuturesRollAmendmentInput extends AmendmentSharedInput {
  rolled_option_month?: string;
  carry?: string;
  shipment_start_date?: Date;
  shipment_end_date?: Date;
  crop_year?: number;
  // Canola futures inputs
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
}

export const FUTURES_ROLL_REQUIRED_ANY_FIELDS: ReadonlyArray<keyof FuturesRollAmendmentInput> = [
  "rolled_option_month",
  "carry",
  "shipment_start_date",
  "shipment_end_date",
  "crop_year",
];

export function futuresRollHasAnyRealField(input: FuturesRollAmendmentInput): boolean {
  return FUTURES_ROLL_REQUIRED_ANY_FIELDS.some((key: keyof FuturesRollAmendmentInput) => {
    const value = input[key];
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
}

export function buildFuturesRollAmendmentRequest(
  expected_current_layer_id: string,
  input: FuturesRollAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  const payload: Record<string, unknown> = {
    ...buildSharedAmendmentPayload(input),
  };

  const setIfPresent = (key: string, value: string | undefined) => {
    const v = emptyToUndefined(value);
    if (v !== undefined) payload[key] = v;
  };

  setIfPresent("rolled_option_month", input.rolled_option_month);
  setIfPresent("carry", input.carry);
  setIfPresent("implied_fx_amount", input.implied_fx_amount);
  setIfPresent("actual_contract_futures", input.actual_contract_futures);
  setIfPresent("actual_contract_futures_step_one", input.actual_contract_futures_step_one);
  setIfPresent("actual_contract_futures_step_two", input.actual_contract_futures_step_two);
  setIfPresent("actual_contract_futures_double_up", input.actual_contract_futures_double_up);
  setIfPresent("final_futures_price", input.final_futures_price);
  setIfPresent("final_futures_expected_price", input.final_futures_expected_price);

  const shipmentStart = amendmentDateToApiString(input.shipment_start_date);
  if (shipmentStart !== undefined) payload.shipment_start_date = shipmentStart;
  const shipmentEnd = amendmentDateToApiString(input.shipment_end_date);
  if (shipmentEnd !== undefined) payload.shipment_end_date = shipmentEnd;
  const finalDate = amendmentDateToApiString(input.final_futures_price_date);
  if (finalDate !== undefined) payload.final_futures_price_date = finalDate;

  if (input.crop_year !== undefined) payload.crop_year = input.crop_year;

  return {
    expected_current_layer_id,
    amendment_type: "futures_roll",
    payload,
  };
}

// ---------------------------------------------------------------------------
// basis_roll amendment
// ---------------------------------------------------------------------------

export interface BasisRollAmendmentInput extends AmendmentSharedInput {
  rolled_basis_month?: string;
  basis_carry?: string;
  shipment_start_date?: Date;
  shipment_end_date?: Date;
  crop_year?: number;
  // Canola futures inputs
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
}

export const BASIS_ROLL_REQUIRED_ANY_FIELDS: ReadonlyArray<keyof BasisRollAmendmentInput> = [
  "rolled_basis_month",
  "basis_carry",
  "shipment_start_date",
  "shipment_end_date",
  "crop_year",
];

export function basisRollHasAnyRealField(input: BasisRollAmendmentInput): boolean {
  return BASIS_ROLL_REQUIRED_ANY_FIELDS.some((key: keyof BasisRollAmendmentInput) => {
    const value = input[key];
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
}

export function buildBasisRollAmendmentRequest(
  expected_current_layer_id: string,
  input: BasisRollAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  const payload: Record<string, unknown> = {
    ...buildSharedAmendmentPayload(input),
  };

  const setIfPresent = (key: string, value: string | undefined) => {
    const v = emptyToUndefined(value);
    if (v !== undefined) payload[key] = v;
  };

  setIfPresent("rolled_basis_month", input.rolled_basis_month);
  setIfPresent("basis_carry", input.basis_carry);
  setIfPresent("implied_fx_amount", input.implied_fx_amount);
  setIfPresent("actual_contract_futures", input.actual_contract_futures);
  setIfPresent("actual_contract_futures_step_one", input.actual_contract_futures_step_one);
  setIfPresent("actual_contract_futures_step_two", input.actual_contract_futures_step_two);
  setIfPresent("actual_contract_futures_double_up", input.actual_contract_futures_double_up);
  setIfPresent("final_futures_price", input.final_futures_price);
  setIfPresent("final_futures_expected_price", input.final_futures_expected_price);

  const shipmentStart = amendmentDateToApiString(input.shipment_start_date);
  if (shipmentStart !== undefined) payload.shipment_start_date = shipmentStart;
  const shipmentEnd = amendmentDateToApiString(input.shipment_end_date);
  if (shipmentEnd !== undefined) payload.shipment_end_date = shipmentEnd;
  const finalDate = amendmentDateToApiString(input.final_futures_price_date);
  if (finalDate !== undefined) payload.final_futures_price_date = finalDate;

  if (input.crop_year !== undefined) payload.crop_year = input.crop_year;

  return {
    expected_current_layer_id,
    amendment_type: "basis_roll",
    payload,
  };
}

// ---------------------------------------------------------------------------
// otc_roll amendment
// ---------------------------------------------------------------------------

export interface OtcRollAmendmentInput extends AmendmentSharedInput {
  option_month?: string;
  option_month_etc_one?: string;
  step_one?: string;
  step_two?: string;
  step_three?: string;
  double_up?: string;
  shipment_start_date?: Date;
  shipment_end_date?: Date;
  end_date?: Date;
  crop_year?: number;
  // Canola futures inputs
  implied_fx_amount?: string;
  actual_contract_futures?: string;
  actual_contract_futures_step_one?: string;
  actual_contract_futures_step_two?: string;
  actual_contract_futures_double_up?: string;
  final_futures_price?: string;
  final_futures_expected_price?: string;
  final_futures_price_date?: Date;
}

export const OTC_ROLL_REQUIRED_ANY_FIELDS: ReadonlyArray<keyof OtcRollAmendmentInput> = [
  "option_month",
  "option_month_etc_one",
  "step_one",
  "step_two",
  "step_three",
  "double_up",
  "shipment_start_date",
  "shipment_end_date",
  "end_date",
  "crop_year",
];

export function otcRollHasAnyRealField(input: OtcRollAmendmentInput): boolean {
  return OTC_ROLL_REQUIRED_ANY_FIELDS.some((key: keyof OtcRollAmendmentInput) => {
    const value = input[key];
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });
}

export function buildOtcRollAmendmentRequest(
  expected_current_layer_id: string,
  input: OtcRollAmendmentInput,
): {
  expected_current_layer_id: string;
  amendment_type: OtcLayerAmendmentType;
  payload: Record<string, unknown>;
} {
  const payload: Record<string, unknown> = {
    ...buildSharedAmendmentPayload(input),
  };

  const setIfPresent = (key: string, value: string | undefined) => {
    const v = emptyToUndefined(value);
    if (v !== undefined) payload[key] = v;
  };

  setIfPresent("option_month", input.option_month);
  setIfPresent("option_month_etc_one", input.option_month_etc_one);
  setIfPresent("step_one", input.step_one);
  setIfPresent("step_two", input.step_two);
  setIfPresent("step_three", input.step_three);
  setIfPresent("double_up", input.double_up);
  setIfPresent("implied_fx_amount", input.implied_fx_amount);
  setIfPresent("actual_contract_futures", input.actual_contract_futures);
  setIfPresent("actual_contract_futures_step_one", input.actual_contract_futures_step_one);
  setIfPresent("actual_contract_futures_step_two", input.actual_contract_futures_step_two);
  setIfPresent("actual_contract_futures_double_up", input.actual_contract_futures_double_up);
  setIfPresent("final_futures_price", input.final_futures_price);
  setIfPresent("final_futures_expected_price", input.final_futures_expected_price);

  const shipmentStart = amendmentDateToApiString(input.shipment_start_date);
  if (shipmentStart !== undefined) payload.shipment_start_date = shipmentStart;
  const shipmentEnd = amendmentDateToApiString(input.shipment_end_date);
  if (shipmentEnd !== undefined) payload.shipment_end_date = shipmentEnd;
  const endDate = amendmentDateToApiString(input.end_date);
  if (endDate !== undefined) payload.end_date = endDate;
  const finalDate = amendmentDateToApiString(input.final_futures_price_date);
  if (finalDate !== undefined) payload.final_futures_price_date = finalDate;

  if (input.crop_year !== undefined) payload.crop_year = input.crop_year;

  return {
    expected_current_layer_id,
    amendment_type: "otc_roll",
    payload,
  };
}
